import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Column } from 'devextreme-react/gantt';
import { Button } from 'devextreme-react/button';
import { formatMessage } from 'devextreme/localization';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import SplitterLayout from 'react-splitter-layout';
import DataGridMVP from '../../DataGridMVP';
import { columnDataGrid, columnDataPart } from './columnDataGrid';
import HeaderButtonMVP from '../../HeaderButtonMVP';
import PopupMVP from '../../PopupMVP';
import styles from './semiProductsTable.module.scss';
import InputModalSemiProduct from '../InputModalSemiProduct';
import InputSemiProductWastesPopup from '../InputModalSemiProduct/InputSemiProductWastesPopup';
import InputModalSavedProducts from '../InputModalSavedProducts';
import byReadyDataGrid from './byReadyDataGrid';
import { normalizeDateToSend, sleep } from '../../../utils/functions';
import { productionStoringNames } from '../constants';
import useWasteColumns from '../../../hooks/useWasteColumns';

const SemiProductsTable = ({
  inputOperationReducer,
  inputReadyProductReducer,
  savedProductsReducer,
  semiProductsReducer,
  tabsReducer: { tabSelect },
  userReducer: { id },
  plantId,
  showSubtable = false,
  zdvlValidate = false,
  data,
  storingName,
  okRequestModal,
  loadingModal,
  getOperation,
  saveOperation,
  loadReadyProducts,
  getReadyProductsBySemi,
  saveReadyProducts,
  getSemiProducts,
  modalErrorOpen,
  useFactWeight,
  setSaveRequest
}) => {
  const [headButtVisible, setHeadButtVisible] = useState({ display: 'none' });
  const [thisDataGridInstance, setThisDataGridInstance] = useState({});
  const [visibleModalInput, setVisibleModalInput] = useState(false);
  const [selectedPieceIds, setSelectedPieceIds] = useState([]);
  const [onModalSave, setOnModalSave] = useState(() => () => {});
  const [validateInstance, setValidateInstance] = useState(null);
  const [dataGridFilter, setDataGridFilter] = useState({
    dateFrom: '',
    dateTo: ''
  });
  const [dataSource, setDataSource] = useState({ selectList: [] });
  const [visibleModalError, setVisibleModalError] = useState(false);
  const [withWasteData, renderWasteCols] = useWasteColumns(data);

  const [isValidFields, setIsValidFields] = useState({
    zdvlLineNumId: true,
    qcId: true,
    pieceId: true,
    quantity: true,
    quantityOff: true
  });
  const blockSaveButton = useRef(false);
  const saveEditableData = useRef(savedProductsReducer.inputPieces);

  const onSelectionChanged = useCallback(({ selectedRowsData }) => {
    if (selectedRowsData.length) {
      setHeadButtVisible({ display: 'flex' });
    } else {
      setHeadButtVisible({ display: 'none' });
    }
    setDataSource((item) => ({
      ...item,
      selectList: selectedRowsData
    }));
    if (selectedRowsData.length) {
      setSelectedPieceIds(selectedRowsData.map((el) => el.pieceNumId));
    } else {
      setSelectedPieceIds([]);
    }
  }, []);

  useEffect(() => {
    if (tabSelect === 8) {
      getReadyProductsBySemi(selectedPieceIds);
    }
  }, [getReadyProductsBySemi, selectedPieceIds, tabSelect]);

  useEffect(() => {
    if (!savedProductsReducer.saving && blockSaveButton.current) {
      blockSaveButton.current = false;
    }
  }, [blockSaveButton, savedProductsReducer.saving]);

  useEffect(() => {
    if (visibleModalError) {
      modalErrorOpen({
        message: formatMessage('textError').wasteWithSemi,
        techInfo: `${formatMessage('textSupportPhone')} 8-800-200-69-39`,
        types: 'error',
        title: formatMessage('textError').warning
      });
      setVisibleModalError(false);
    }
  }, [modalErrorOpen, visibleModalError]);

  const closeHeaderButton = useCallback(() => {
    thisDataGridInstance.instance.clearSelection();
    setHeadButtVisible({ display: 'none' });
    setIsValidFields({
      zdvlLineNumId: true,
      qcId: true,
      pieceId: true,
      quantity: true,
      quantityOff: true
    });
  }, [thisDataGridInstance.instance]);

  const onClickInputData = useCallback(async () => {
    const isSameZdvl = Array.from(new Set(dataSource.selectList.map((el) => el.zdvlOrderLineId)))
      .length <= 1;
    if (zdvlValidate && !isSameZdvl) {
      modalErrorOpen({
        message: 'Не все рулоны привязаны к одной позиции ZDVL-заказа',
        techInfo: '',
        types: 'error',
        title: 'Ошибка'
      });
    } else if (storingName === productionStoringNames.dataGridInputTrim) {
      await getOperation({
        items: dataSource.selectList.map((item) => item.pieceNumId),
        operationType: 1
      });
      setVisibleModalInput(true);
      closeHeaderButton();
    } else if (storingName === productionStoringNames.dataGridInputWaste) {
      await getOperation({
        items: dataSource.selectList.map((item) => item.pieceNumId),
        operationType: 2
      });
      setVisibleModalInput(true);
      closeHeaderButton();
    } else if (storingName === productionStoringNames.dataGridInputAllWaste) {
      await getOperation({
        items: dataSource.selectList.map((item) => item.pieceNumId),
        operationType: 5
      });
      setVisibleModalInput(true);
      closeHeaderButton();
    } else {
      await loadReadyProducts(
        dataSource.selectList.map((item) => item.pieceNumId)
      );
      setVisibleModalInput(true);
    }
  }, [
    closeHeaderButton,
    dataSource.selectList,
    getOperation,
    loadReadyProducts,
    modalErrorOpen,
    storingName,
    zdvlValidate
  ]);

  const onModalHiding = useCallback(() => {
    setVisibleModalInput(false);
  }, []);

  useEffect(() => {
    if (tabSelect === 8) {
      getSemiProducts({
        ...dataGridFilter,
        plantId
      });
    }
  }, [
    getSemiProducts,
    plantId,
    savedProductsReducer.saving,
    storingName,
    inputReadyProductReducer.okRequest,
    inputOperationReducer.saveOperation.okRequest,
    dataGridFilter,
    tabSelect
  ]);

  const onSaveOperationClick = useCallback(() => {
    if (storingName === productionStoringNames.dataGridInputTrim) {
      saveOperation({
        operationPieces:
          inputOperationReducer.operationMaterials.operationPieces,
        operationType: 1,
        zdvlLineNumId: 0
      });
      setVisibleModalInput(false);
      getSemiProducts({
        ...dataGridFilter,
        plantId
      });
      closeHeaderButton();
    } else if (storingName === productionStoringNames.dataGridInputWaste) {
      saveOperation({
        operationPieces:
          inputOperationReducer.operationMaterials.operationPieces,
        operationType: 2,
        zdvlLineNumId: 0
      });
      setVisibleModalInput(false);
      getSemiProducts({
        ...dataGridFilter,
        plantId
      });
      closeHeaderButton();
    } else if (validateInstance) {
      new Promise((res) => {
        if (validateInstance.validate().status !== 'pending') {
          res(validateInstance.validate());
        }
      }).then((result) => {
        if (result.isValid && !blockSaveButton.current) {
          saveReadyProducts({
            inputPieces: saveEditableData.current || savedProductsReducer.inputPieces,
            plantOperationAttribsInheritDtos: savedProductsReducer.plantOperationAttribsInheritDtos,
            length: savedProductsReducer.length,
            mark: savedProductsReducer.mark,
            normativeDoc: savedProductsReducer.normativeDoc,
            operationNumId: savedProductsReducer.operationNumId,
            operationType: savedProductsReducer.operationType,
            pieceId: savedProductsReducer.pieceId,
            pieceNumId: savedProductsReducer.pieceNumId,
            profileSize: savedProductsReducer.profileSize,
            qcDate: normalizeDateToSend(savedProductsReducer.qcDate),
            qcId: savedProductsReducer.qcId,
            qty: savedProductsReducer.qty,
            thickness: savedProductsReducer.thickness,
            quantity: useFactWeight ? null : savedProductsReducer.quantity,
            quantityOff: savedProductsReducer.quantityOff,
            zdvlLineNumId: savedProductsReducer.zdvlLineNumId
          });
          blockSaveButton.current = true;
        }
      });
      closeHeaderButton();
    }
  }, [
    blockSaveButton,
    storingName,
    saveOperation,
    inputOperationReducer.operationMaterials.operationPieces,
    getSemiProducts,
    dataGridFilter,
    plantId,
    validateInstance,
    saveReadyProducts,
    savedProductsReducer,
    closeHeaderButton,
    saveEditableData,
    useFactWeight
  ]);

  useEffect(() => {
    if (savedProductsReducer.okSaveRequest) {
      setVisibleModalInput(false);
      setSaveRequest(false);
    }
  }, [savedProductsReducer, setSaveRequest]);

  const tableTitle = useMemo(() => {
    if (storingName === productionStoringNames.dataGridInputTrim) {
      return formatMessage('textButton').inputTrim;
    }
    if (storingName === productionStoringNames.dataGridInputWaste) {
      return formatMessage('textButton').inputWaste;
    }
    if (storingName === productionStoringNames.dataGridInputAllWaste) {
      return formatMessage('textButton').inputAllWaste;
    }
    return formatMessage('textButton').inputReady;
  }, [storingName]);

  const onSearchChanged = useCallback(() => {
    closeHeaderButton();
    setDataSource((item) => ({
      ...item,
      selectList: []
    }));
  }, [closeHeaderButton]);

  const onSaveClickHandler = async () => {
    await sleep(100);
    let validFields = true;
    if (storingName === productionStoringNames.dataGridInputReady) {
      if (savedProductsReducer.zdvlLineNumId === ''
        || savedProductsReducer.zdvlLineNumId === null) {
        setIsValidFields((item) => ({
          ...item,
          zdvlLineNumId: false
        }));
        validFields = false;
      }
      if (savedProductsReducer.qcId === ''
        || savedProductsReducer.qcId === null) {
        setIsValidFields((item) => ({
          ...item,
          qcId: false
        }));
        validFields = false;
      }
      if (savedProductsReducer.pieceId === ''
        || savedProductsReducer.pieceId === null) {
        setIsValidFields((item) => ({
          ...item,
          pieceId: false
        }));
        validFields = false;
      }
      if (savedProductsReducer.quantity === ''
        || savedProductsReducer.quantity === null) {
        setIsValidFields((item) => ({
          ...item,
          quantity: false
        }));
        validFields = false;
      }
      if (savedProductsReducer.quantityOff === ''
        || savedProductsReducer.quantityOff === null) {
        setIsValidFields((item) => ({
          ...item,
          quantityOff: false
        }));
        validFields = false;
      }
      if (!validFields) {
        modalErrorOpen({
          message: `Не все поля заполнены корректно, проверьте поля "Позиция заказа",
          "№ партии ГП (Сертификата)", "№ УЕ", а так же теор. и факт. вес, при наличии!`,
          techInfo: '',
          types: 'error',
          title: 'Ошибка'
        });
      } else {
        await onModalSave();
        await sleep(1000);
        await onSaveOperationClick();
      }
    } else {
      onSaveOperationClick();
    }
  };

  useEffect(() => {
    if (id !== null && tabSelect === 8 && okRequestModal && visibleModalInput) {
      thisDataGridInstance.instance.clearSelection();
    }
  }, [id, thisDataGridInstance, okRequestModal, tabSelect, visibleModalInput]);

  const onClickCancelBtn = useCallback(() => {
    onModalHiding();
    closeHeaderButton();
  }, [closeHeaderButton, onModalHiding]);

  // Обновление данных
  const onClickUpdate = useCallback(() => {
    getSemiProducts({
      ...dataGridFilter,
      plantId
    });
  }, [dataGridFilter, getSemiProducts, plantId]);

  const onChangeTaskFilter = useCallback((filter) => {
    setDataGridFilter({ dateFrom: filter.dateFrom || '', dateTo: filter.dateTo || '' });
  }, [setDataGridFilter]);

  const semiPiecesTable = useMemo(
    () => (
      <DataGridMVP
        dataSource={withWasteData}
        keyExpr="pieceNumId"
        setDataGridInstance={setThisDataGridInstance}
        onSelectionChanged={onSelectionChanged}
        setStateStoringName={storingName}
        className={styles.topTable}
        filterDataGrid={dataGridFilter}
        setFilterDataGrid={onChangeTaskFilter}
        onSearchChanged={onSearchChanged}
        isDataLoading={
          savedProductsReducer.saving
          || inputOperationReducer.loading
          || semiProductsReducer.loading
          || inputReadyProductReducer.loading
          || inputOperationReducer.getOperation.loading
          || inputOperationReducer.saveOperation.loading
        }
        dataGridOnOptionChanged={() => {}}
        tText={formatMessage('textPeriod')}
        onClickUpdate={onClickUpdate}
        visibleButtonUpdate
        altWeightNettoField="quantity"
        altWeightBruttoField="quantityRemain"
      >
        {columnDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch
            allowFiltering
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
        ))}
        {renderWasteCols}

        {columnDataPart.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch
            allowFiltering
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
        ))}
      </DataGridMVP>
    ),
    [
      withWasteData,
      onSelectionChanged,
      storingName,
      dataGridFilter,
      onSearchChanged,
      savedProductsReducer.saving,
      inputOperationReducer.loading,
      inputOperationReducer.getOperation.loading,
      inputOperationReducer.saveOperation.loading,
      semiProductsReducer.loading,
      inputReadyProductReducer.loading,
      onClickUpdate,
      renderWasteCols,
      onChangeTaskFilter
    ]
  );

  const readyBySemiTable = useMemo(
    () => (
      <DataGridMVP
        dataSource={savedProductsReducer.readyProductsBySemi}
        keyExpr="pieceNumId"
        selectionMode="none"
        setDataGridInstance={() => {}}
        onSelectionChanged={() => {}}
        setStateStoringName={() => {}}
        filterDataGrid
        storingName="subtableBySemiGrid"
        setFilterDataGrid={() => {}}
        className={styles.bottomTable}
        dataGridOnOptionChanged={() => {}}
        tText={formatMessage('textPeriod')}
        onClickUpdate={() => {}}
      >
        {byReadyDataGrid.map((item) => (
          <Column
            dataField={item.dataField}
            caption={item.caption}
            visible={!item.notVisible}
            allowSearch
            allowFiltering
            cellRender={item.cellRender}
            key={item.dataField}
            dataType={item.dataType}
            cssClass={item.cssClass}
            width={item.width ? item.width : undefined}
          />
        ))}
      </DataGridMVP>
    ),
    [savedProductsReducer.readyProductsBySemi]
  );

  return (
    <>
      <SplitterLayout>
        <div className={styles.topSide}>
          <HeaderButtonMVP headButtVisibol={headButtVisible}>
            <Button
              icon={'close'}
              stylingMode={'text'}
              onClick={closeHeaderButton}
            />
            <span className={'span-first'}>
              {`Выбрано позиций - ${dataSource.selectList.length}`}
            </span>
            {data.length > 0 && (
              <>
                <Button
                  text={formatMessage('textButton').inputData}
                  type={'default'}
                  stylingMode={'contained'}
                  onClick={onClickInputData}
                />
              </>
            )}
          </HeaderButtonMVP>
          {semiPiecesTable}
        </div>
        {showSubtable && (
          <div className={styles.bottomSide}>{readyBySemiTable}</div>
        )}
      </SplitterLayout>
      {storingName === productionStoringNames.dataGridInputAllWaste ? (
        <InputSemiProductWastesPopup
          storingName={storingName}
          onClose={onModalHiding}
          inputOperationReducer={inputOperationReducer}
          saveOperation={saveOperation}
          getSemiProducts={getSemiProducts}
          visibleModalInput={visibleModalInput}
          setVisibleModalError={setVisibleModalError}
          dataGridFilter={dataGridFilter}
          loadingModal={loadingModal}
          plantId={plantId}
        />
      ) : (
        <PopupMVP
          visible={visibleModalInput}
          onHiding={onModalHiding}
          title={tableTitle}
          width={'90%'}
          height={'auto'}
          resizeEnabled={false}
        >
          <div className="formData">
            {storingName === productionStoringNames.dataGridInputReady ? (
              <InputModalSavedProducts
                plantId={plantId}
                setOnModalSave={setOnModalSave}
                selectedPieces={dataSource.selectList}
                setValidateInstance={setValidateInstance}
                visibleModal={visibleModalInput}
                useFactWeight={useFactWeight}
                isValidFields={isValidFields}
                saveEditableData={saveEditableData}
              />
            ) : (
              <InputModalSemiProduct storingName={storingName} />
            )}
            <div className="formButton">
              <Button
                text={formatMessage('textButton').reject}
                type="default"
                stylingMode="text"
                onClick={onClickCancelBtn}
              />
              <Button
                type="default"
                stylingMode="contained"
                onClick={onSaveClickHandler}
                width={260}
                disabled={blockSaveButton.current}
              >
                <LoadIndicator className="indicator" visible={loadingModal} />
                <span>{formatMessage('textButton').save}</span>
              </Button>
            </div>
          </div>
        </PopupMVP>
      )}
    </>
  );
};

export default React.memo(SemiProductsTable);
